import type { UseMutationResult } from "@tanstack/react-query"
import type { UseFormReturn } from "react-hook-form"
import { toast } from "sonner"
import type {
  CartShippingAddressInput,
  CartShippingAddressResponse,
} from "@/api/cart"
import type { CheckoutFormValues } from "@/app/(checkout)/finalizar-comprar/page"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/utilities"

interface BillingDetailsSectionProps {
  form: UseFormReturn<CheckoutFormValues>
  updateShippingAddressMutation: UseMutationResult<
    CartShippingAddressResponse,
    Error,
    CartShippingAddressInput,
    { toastId: string | number }
  >
  onShipToDifferentAddressChange: (checked: boolean) => void
}

const canSubmitDeliveryAddress = (values: CheckoutFormValues): boolean => {
  if (!values.shipToDifferentAddress) {
    return false
  }

  return (
    values.shipping.city.trim().length > 0 &&
    values.shipping.postcode.trim().length > 0
  )
}

export const BillingDetailsSection = ({
  form,
  updateShippingAddressMutation,
  onShipToDifferentAddressChange,
}: BillingDetailsSectionProps) => {
  const shipToDifferentAddress = form.watch("shipToDifferentAddress")
  const shipToDifferentAddressField = form.register("shipToDifferentAddress")

  const applyDeliveryAddress = () => {
    const values = form.getValues()

    if (!canSubmitDeliveryAddress(values)) {
      toast.error("Preencha cidade e CEP para calcular a entrega.")
      return
    }

    updateShippingAddressMutation.mutate({
      country: values.shipping.country,
      state: values.shipping.state,
      city: values.shipping.city,
      postcode: values.shipping.postcode,
    })
  }

  return (
    <section className='space-y-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-xs sm:p-6'>
      <div className='flex items-center justify-between gap-3'>
        <h2 className='font-semibold text-lg text-zinc-900 tracking-tight sm:text-xl'>
          Detalhes de cobranca
        </h2>
        <span className='rounded-full bg-zinc-100 px-3 py-1 font-medium text-xs text-zinc-600 uppercase tracking-[0.12em]'>
          Passo 1 de 2
        </span>
      </div>

      <div className='space-y-5'>
        <div className='space-y-2'>
          <label className='font-medium text-sm text-zinc-700' htmlFor='email'>
            Endereco de e-mail <span className='text-destructive'>*</span>
          </label>
          <Input
            className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
            id='email'
            type='email'
            {...form.register("billing.email")}
          />
        </div>

        <div className='grid gap-4 sm:grid-cols-2'>
          <div className='space-y-2'>
            <label
              className='font-medium text-sm text-zinc-700'
              htmlFor='first-name'
            >
              Nome <span className='text-destructive'>*</span>
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='first-name'
              {...form.register("billing.firstName")}
            />
          </div>
          <div className='space-y-2'>
            <label
              className='font-medium text-sm text-zinc-700'
              htmlFor='last-name'
            >
              Sobrenome <span className='text-destructive'>*</span>
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='last-name'
              {...form.register("billing.lastName")}
            />
          </div>
        </div>

        <div className='grid gap-4 sm:grid-cols-2'>
          <div className='space-y-2'>
            <label className='font-medium text-sm text-zinc-700' htmlFor='cpf'>
              CPF <span className='text-destructive'>*</span>
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='cpf'
              placeholder='000.000.000-00'
              {...form.register("billing.cpf")}
            />
          </div>
          <div className='space-y-2'>
            <label
              className='font-medium text-sm text-zinc-700'
              htmlFor='phone'
            >
              Telefone <span className='text-destructive'>*</span>
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='phone'
              placeholder='(00) 00000-0000'
              {...form.register("billing.phone")}
            />
          </div>
        </div>

        <div className='grid gap-4 sm:grid-cols-[150px_1fr]'>
          <div className='space-y-2'>
            <label className='font-medium text-sm text-zinc-700' htmlFor='cep'>
              CEP <span className='text-destructive'>*</span>
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='cep'
              placeholder='00000-000'
              {...form.register("billing.postcode")}
            />
          </div>
          <div className='space-y-2'>
            <label
              className='font-medium text-sm text-zinc-700'
              htmlFor='street'
            >
              Rua <span className='text-destructive'>*</span>
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='street'
              placeholder='Nome da rua'
              {...form.register("billing.address1")}
            />
          </div>
        </div>

        <div className='grid gap-4 sm:grid-cols-2'>
          <div className='space-y-2'>
            <label
              className='font-medium text-sm text-zinc-700'
              htmlFor='number'
            >
              Numero <span className='text-destructive'>*</span>
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='number'
              {...form.register("billing.number")}
            />
          </div>
          <div className='space-y-2'>
            <label
              className='font-medium text-sm text-zinc-700'
              htmlFor='complement'
            >
              Complemento (opcional)
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='complement'
              placeholder='Apartamento, suite, unidade, etc.'
              {...form.register("billing.address2")}
            />
          </div>
        </div>

        <div className='grid gap-4 sm:grid-cols-[1fr_120px]'>
          <div className='space-y-2'>
            <label
              className='font-medium text-sm text-zinc-700'
              htmlFor='district'
            >
              Bairro <span className='text-destructive'>*</span>
            </label>
            <Input
              className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
              id='district'
              {...form.register("billing.district")}
            />
          </div>
          <div className='space-y-2'>
            <label
              className='font-medium text-sm text-zinc-700'
              htmlFor='state'
            >
              Estado <span className='text-destructive'>*</span>
            </label>
            <select
              className={cn(
                "w-full",
                "h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary"
              )}
              id='state'
              {...form.register("billing.state")}
            >
              <option value='SP'>Sao Paulo</option>
              <option value='RJ'>Rio de Janeiro</option>
              <option value='MG'>Minas Gerais</option>
            </select>
          </div>
        </div>

        <div className='space-y-2'>
          <label className='font-medium text-sm text-zinc-700' htmlFor='notes'>
            Observacoes do pedido (opcional)
          </label>
          <textarea
            className='flex min-h-28 w-full rounded-xl border border-zinc-300 bg-white/90 px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'
            id='notes'
            placeholder='Observacoes sobre seu pedido, ex: horario de entrega, ponto de referencia, etc.'
            {...form.register("billing.notes")}
          />
        </div>

        <div className='rounded-xl border border-destructive/50 border-dashed bg-destructive/5 px-4 py-3 text-destructive text-sm'>
          Informe seu endereco para ver as opcoes de entrega.
        </div>

        <label className='flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-700'>
          <input
            className='size-4'
            type='checkbox'
            {...shipToDifferentAddressField}
            onChange={(event) => {
              shipToDifferentAddressField.onChange(event)
              onShipToDifferentAddressChange(event.target.checked)
            }}
          />
          Meu endereco de entrega e diferente do endereco de cobranca
        </label>

        {shipToDifferentAddress ? (
          <section className='space-y-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-4 sm:p-5'>
            <div className='flex items-center justify-between gap-3'>
              <h3 className='font-semibold text-base text-zinc-900'>
                Endereco de entrega
              </h3>
              <span className='rounded-full bg-white px-3 py-1 font-medium text-xs text-zinc-600 uppercase tracking-[0.12em]'>
                Passo 2 de 2
              </span>
            </div>
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <label
                  className='font-medium text-sm text-zinc-700'
                  htmlFor='delivery-city'
                >
                  Cidade <span className='text-destructive'>*</span>
                </label>
                <Input
                  className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
                  id='delivery-city'
                  placeholder='Ex: Sao Paulo'
                  {...form.register("shipping.city")}
                />
              </div>
              <div className='space-y-2'>
                <label
                  className='font-medium text-sm text-zinc-700'
                  htmlFor='delivery-postcode'
                >
                  CEP <span className='text-destructive'>*</span>
                </label>
                <Input
                  className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
                  id='delivery-postcode'
                  placeholder='00000-000'
                  {...form.register("shipping.postcode")}
                />
              </div>
            </div>
            <div className='grid gap-4 sm:grid-cols-[1fr_120px]'>
              <div className='space-y-2'>
                <label
                  className='font-medium text-sm text-zinc-700'
                  htmlFor='delivery-street'
                >
                  Rua
                </label>
                <Input
                  className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
                  id='delivery-street'
                  placeholder='Nome da rua'
                  {...form.register("shipping.address1")}
                />
              </div>
              <div className='space-y-2'>
                <label
                  className='font-medium text-sm text-zinc-700'
                  htmlFor='delivery-number'
                >
                  Numero
                </label>
                <Input
                  className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
                  id='delivery-number'
                  {...form.register("shipping.number")}
                />
              </div>
            </div>
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <label
                  className='font-medium text-sm text-zinc-700'
                  htmlFor='delivery-neighborhood'
                >
                  Bairro
                </label>
                <Input
                  className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
                  id='delivery-neighborhood'
                  {...form.register("shipping.neighborhood")}
                />
              </div>
              <div className='space-y-2'>
                <label
                  className='font-medium text-sm text-zinc-700'
                  htmlFor='delivery-complement'
                >
                  Complemento
                </label>
                <Input
                  className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
                  id='delivery-complement'
                  placeholder='Apartamento, suite, unidade, etc.'
                  {...form.register("shipping.address2")}
                />
              </div>
            </div>
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <label
                  className='font-medium text-sm text-zinc-700'
                  htmlFor='delivery-state'
                >
                  Estado
                </label>
                <select
                  className={cn(
                    "w-full",
                    "h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary"
                  )}
                  id='delivery-state'
                  {...form.register("shipping.state")}
                >
                  <option value='SP'>Sao Paulo</option>
                  <option value='RJ'>Rio de Janeiro</option>
                  <option value='MG'>Minas Gerais</option>
                </select>
              </div>
              <div className='space-y-2'>
                <label
                  className='font-medium text-sm text-zinc-700'
                  htmlFor='delivery-country'
                >
                  Pais
                </label>
                <Input
                  className='h-11 rounded-xl border-zinc-300 bg-white/90 focus-visible:border-primary'
                  id='delivery-country'
                  placeholder='BR'
                  {...form.register("shipping.country")}
                />
              </div>
            </div>
            <Button
              disabled={
                updateShippingAddressMutation.isPending ||
                !canSubmitDeliveryAddress(form.getValues())
              }
              onClick={applyDeliveryAddress}
              type='button'
              variant='outline'
            >
              {updateShippingAddressMutation.isPending
                ? "Atualizando..."
                : "Recalcular frete"}
            </Button>
          </section>
        ) : null}
      </div>
    </section>
  )
}
